import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { paymentStatus } from "@/lib/api";
import { ArrowUpRight, CheckCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sid) { setError("Missing session"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await paymentStatus(sid);
        if (r.payment_status === "paid") { setStatus(r); return; }
        if (r.payment_status === "failed" || r.payment_status === "expired") { setError("Payment not completed"); return; }
        if (attempts++ < 20) setTimeout(poll, 2000);
        else setError("Still processing. Check your email in a moment.");
      } catch { if (attempts++ < 20) setTimeout(poll, 2000); }
    };
    poll();
  }, [sid]);

  return (
    <div data-testid="payment-success" className="pt-40 md:pt-52 pb-32 mx-auto max-w-[900px] px-5 md:px-10 min-h-[70vh]">
      <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Payment</div>
      {!status && !error && (
        <>
          <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-7xl">Processing your ticket…</h1>
          <p className="mt-6 font-body text-lg" style={{ color: "var(--ss-cream-dim)" }}>Just a moment while we confirm with Stripe.</p>
        </>
      )}
      {status && status.type !== "gift_card" && (
        <>
          <div className="flex items-center gap-3 mt-4"><CheckCircle size={32} style={{ color: "var(--ss-rust)" }} /></div>
          <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-7xl">You're in.</h1>
          <p className="mt-6 font-body text-lg md:text-xl" style={{ color: "var(--ss-cream-dim)" }}>
            Confirmed for <span style={{ color: "var(--ss-cream)" }}>{status.event_name}</span> — {status.quantity} ticket{status.quantity > 1 ? "s" : ""}, ${Number(status.amount).toFixed(2)}. A confirmation email is on the way.
          </p>
          <div className="mt-10 flex gap-4">
            <Link to="/events" data-testid="success-events-btn" className="btn-outline">Back to Events</Link>
            <Link to="/" className="btn-primary">Home <ArrowUpRight className="arrow" size={14} /></Link>
          </div>
        </>
      )}
      {status && status.type === "gift_card" && (
        <>
          <div className="flex items-center gap-3 mt-4"><CheckCircle size={32} style={{ color: "var(--ss-rust)" }} /></div>
          <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-7xl">Gift sent.</h1>
          <p className="mt-6 font-body text-lg md:text-xl" style={{ color: "var(--ss-cream-dim)" }}>
            A ${Number(status.amount).toFixed(0)} Simply Social gift card is on its way. We've emailed the unique gift code to your inbox.
          </p>
          {status.gift_code && (
            <div className="mt-8 p-6 border inline-block" data-testid="gift-code-display"
                 style={{ borderColor: "var(--ss-rust)", background: "var(--ss-bg-2)" }}>
              <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Your Gift Code</div>
              <div className="font-display text-3xl md:text-4xl mt-2 tracking-widest">{status.gift_code}</div>
            </div>
          )}
          <div className="mt-10 flex gap-4">
            <Link to="/gift-cards" data-testid="success-more-gifts-btn" className="btn-outline">Buy Another</Link>
            <Link to="/" className="btn-primary">Home <ArrowUpRight className="arrow" size={14} /></Link>
          </div>
        </>
      )}
      {error && (
        <>
          <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-7xl">Hmm.</h1>
          <p className="mt-6 font-body text-lg" style={{ color: "var(--ss-cream-dim)" }}>{error}</p>
          <Link to="/events" className="btn-primary mt-8 inline-flex">Back to Events <ArrowUpRight className="arrow" size={14} /></Link>
        </>
      )}
    </div>
  );
}
